/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { faEye, faUpload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import { ReactNode, useMemo, type FC } from 'react'
import { ButtonGroup, Col, FloatingLabel, Form, Row } from 'react-bootstrap'
import Button from 'react-bootstrap/Button'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import FileUploader from '../FileUploader'

export const validationSchema = yup.object().shape({
  description: yup.string().min(1).max(255),
  file: yup.mixed().nullable(),
  background: yup.mixed().notRequired(),
  status: yup.string().oneOf(['active', 'inactive', 'deleted']).default('inactive'),
  priority: yup.string().oneOf(['primary', 'secondary']).default('primary'),
  publishedIn: yup.string().default(() => moment().format('YYYY-MM-DDTHH:mm')),
  unpublishedIn: yup.string().default(() => moment().add(5, 'days').format('YYYY-MM-DDTHH:mm'))
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormEncarteHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormEncarteHandleSubmit,
  initialValues?: Partial<ValidationSchema>,
  isLoading?: boolean,
  readOnly?: boolean,
  customFooterMessage?: ReactNode
}

/**
 * FormEncarte is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormEncarte from './src/views/PostEdit/FormEncarte.tsx'
 *
 * const MyComponent = () => (
 *   <FormEncarte />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormEncarte: FC<Props> = ({
  onSubmit = () => { },
  initialValues = {},
  isLoading = false,
  readOnly = false,
  customFooterMessage = null
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: false,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  return (
    <Form onSubmit={formik.handleSubmit} className='mt-5'>
      <Form.Group as={Row} className="mb-4">
        <Col sm={4}>
          <Form.Label>
            Geral
          </Form.Label>
          <br />
          <Form.Text muted>
            Preencha os campos obrigatórios para cadastrar um encarte. Os campos de vigência.
          </Form.Text>
        </Col>
        <Col sm={8}>
          <Row>
            <Form.Group as={Col} md={12} className='mb-3'>
              <FloatingLabel label='Descrição (máx: 160 caracteres)'>
                <Form.Control
                  type='text'
                  name='description'
                  className='form-control'
                  value={formik?.values?.description ?? ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting || isLoading || readOnly}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group as={Col} md={6} className='mb-3'>
              <FloatingLabel label='Visibilidade'>
                <Form.Select
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting || isLoading}
                  name='status'
                >
                  <option>Selecione uma opção</option>
                  <option value='active'>🌎 Pública</option>
                  <option value='inactive'>🔒 Privada</option>
                </Form.Select>
              </FloatingLabel>
            </Form.Group>
            <Form.Group as={Col} md={6} className='mb-3'>
              <FloatingLabel label='Prioridade'>
                <Form.Select
                  value={formik.values.priority}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting || isLoading}
                  name='priority'
                >
                  <option>Informe a prioridade do encarte</option>
                  <option value='primary'>Primário</option>
                  <option value='secondary'>Secundário</option>
                </Form.Select>
              </FloatingLabel>
            </Form.Group>
          </Row>
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-4">
        <Col sm={4}>
          <Form.Label>
            Vigência
          </Form.Label>
          <br />
          <Form.Text muted>
            Defina as datas de ativação e desativação do encarte. É possível programar o encarte para ser ativado ou desativado automaticamente em uma data futura.
          </Form.Text>
        </Col>
        <Col sm={8}>
          <Row>
            <Form.Group as={Col} md={6}>
              <FloatingLabel label='Ativar em'>
                <Form.Control
                  type='datetime-local'
                  name='publishedIn'
                  value={formik?.values?.publishedIn ?? ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting || isLoading || readOnly}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <FloatingLabel label='Desativar em'>
                <Form.Control
                  type='datetime-local'
                  name='unpublishedIn'
                  value={formik?.values?.unpublishedIn ?? ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting || isLoading || readOnly}
                />
              </FloatingLabel>
            </Form.Group>
          </Row>
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-4">
        <Col sm={4}>
          <Form.Label>
            Arquivo do encarte
          </Form.Label>
          <br />
          <Form.Text muted>
            Faça upload do arquivo PDF do encarte. O arquivo deve ter no máximo 24 MB. Você pode pré-visualizar o arquivo enviado antes de salvar as alterações.
          </Form.Text>
        </Col>
        <Col sm={8}>
          <Row>
            <Form.Group as={Col} md={12} className='mb-3'>
              <Form.Label style={{ paddingRight: 20 }}>Arquivo PDF do encarte (máx.: 24 MB)</Form.Label>
              <ButtonGroup>
                <Button
                  as='a'
                  size='sm'
                  variant="outline-primary"
                  href={(formik?.values?.file as any)?.url} target='_blank' rel='noreferrer'
                  disabled={formik.isSubmitting || isLoading || !(formik?.values?.file as any)?.url}
                >
                  <FontAwesomeIcon icon={faEye} />
                  {' '}
                  Pré-visualizar
                </Button>
                <FileUploader
                  variant='outline-primary'
                  accept='.pdf'
                  size='sm'
                  disabled={formik.isSubmitting || isLoading || readOnly}
                  onReceiveFiles={([{ file }]) => {
                    formik.setFieldValue('file', file)
                  }}
                >
                  <FontAwesomeIcon icon={faUpload} />
                  {' '}
                  Enviar arquivo
                </FileUploader>
              </ButtonGroup>
            </Form.Group>
          </Row>
        </Col>
      </Form.Group>
      <Form.Group as={Row} className="mb-4">
        <Col sm={4}>
          <Form.Label>
            Plano de fundo
          </Form.Label>
          <br />
          <Form.Text muted>
            Defina o plano de fundo do encarte. O plano de fundo é uma imagem que será exibida atrás do conteúdo do encarte. A imagem deve ter resolução de 640x360 pixels.
          </Form.Text>
        </Col>
        <Col sm={8}>
          <Row>
            <Form.Group as={Col} md={6} className='mb-3'>
              <Form.Label style={{ paddingRight: 20 }}>
                Plano de fundo do encarte (obrigatório 640x360px)
              </Form.Label>
              <FileUploader
                variant='outline-primary'
                accept='image/*'
                size='sm'
                resolutionHeight={640}
                resolutionWidth={360}
                resolutionType='absolute'
                disabled={formik.isSubmitting || isLoading || readOnly}
                onReceiveFiles={([{ file }]) =>
                  formik.setFieldValue('background', file)
                }
                onError={error => console.error(error.message)}
              >
                <FontAwesomeIcon icon={faUpload} />
                {' '}
                Enviar arquivo
              </FileUploader>
            </Form.Group>
          </Row>
        </Col>
      </Form.Group>
      <Row>
        <Col md={12}>
          <Button
            variant='outline-secondary'
            style={{ marginRight: 10 }}
            onClick={() => navigate('/encartes')}
            disabled={formik.isSubmitting || isLoading}
          >
            Voltar
          </Button>
          <Button
            variant='outline-primary'
            type='submit'
            disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
          >
            Salvar
          </Button>
          {' '}
          {customFooterMessage}
        </Col>
      </Row>
    </Form>
  )
}

export default FormEncarte
